import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Mail, Phone, Globe, Pencil, Trash2, Send, MessageSquarePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StageBadge, ScoreChip } from "@/components/Layout";
import LeadFormDialog from "@/components/LeadFormDialog";
import { FindEmailButton } from "@/components/FindEmail";
import ComposeDialog from "@/components/ComposeDialog";
import { api, errMsg, STAGES, fullName, ago, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const OUTCOMES = [["none", "Open"], ["meeting_booked", "Meeting booked"], ["won", "Won"], ["lost", "Lost"], ["no_response", "No response"], ["unsubscribed", "Unsubscribed"]];

function Notes({ lead, reload }) {
  const [text, setText] = useState("");
  const add = async () => { if (!text.trim()) return; await api.post(`/leads/${lead.id}/notes`, { text }); setText(""); reload(); };
  return (
    <div className="space-y-3">
      <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a sales note…" rows={3} data-testid="note-input" />
      <Button size="sm" onClick={add} data-testid="note-add-btn"><MessageSquarePlus size={14} className="mr-1" />Add note</Button>
      <div className="space-y-2 pt-2" data-testid="notes-list">
        {(lead.notes || []).map((n) => (
          <div key={n.id} className="border-l-2 border-blue-500 bg-white border rounded-r-lg p-3" data-testid={`note-${n.id}`}>
            <div className="text-xs text-slate-500 mb-1"><b className="text-slate-700">{n.user_name}</b> · {fmtDate(n.created_at)}</div>
            <div className="text-sm whitespace-pre-wrap">{n.text}</div>
          </div>
        ))}
        {!lead.notes?.length && <div className="text-sm text-slate-400">No notes yet.</div>}
      </div>
    </div>
  );
}

function SimulateReply({ lead, reload }) {
  const [body, setBody] = useState("");
  const go = async () => {
    try { const { data } = await api.post("/inbox/simulate", { lead_id: lead.id, body, subject: "Re: your email" }); toast.success(`Reply classified: ${data.classification.category}`); setBody(""); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="border border-dashed rounded-lg p-3 space-y-2">
      <div className="label-xs">Test: simulate an inbound reply (until M365 is connected)</div>
      <Input value={body} onChange={(e) => setBody(e.target.value)} placeholder="e.g. Sounds interesting, can we talk Thursday?" data-testid="simulate-reply-input" />
      <Button size="sm" variant="outline" onClick={go} disabled={!body} data-testid="simulate-reply-btn">Simulate reply</Button>
    </div>
  );
}

export default function LeadDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [lead, setLead] = useState(null);
  const [edit, setEdit] = useState(false);
  const [compose, setCompose] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [camps, setCamps] = useState([]);
  const { user } = useAuth();
  const load = () => api.get(`/leads/${id}`).then((r) => setLead(r.data));
  useEffect(() => { load(); }, [id]); // eslint-disable-line
  useEffect(() => { api.get("/campaigns").then((r) => setCamps(r.data)); }, []);
  if (!lead) return <div className="p-10 label-xs">Loading…</div>;
  const setCampaign = async (v) => {
    try {
      await api.patch(`/leads/${id}`, { campaign_id: v === "none" ? null : v });
      const c = camps.find((x) => x.id === v);
      toast.success(v === "none" ? "Saved: removed from campaign" : `Saved: assigned to ${c?.name}${c?.status === "active" ? ". Its first email will wait in the Outbox for review." : " (campaign is not active, so no emails are queued)"}`);
      window.dispatchEvent(new Event("outbox-changed")); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const describe = async () => {
    setAiBusy(true);
    try { await api.post(`/leads/${id}/ai-describe`); toast.success("AI description generated"); load(); } catch (e) { toast.error(errMsg(e)); } finally { setAiBusy(false); }
  };
  const setStage = async (stage) => { await api.post(`/leads/${id}/stage`, { stage }); load(); };
  const setOutcome = async (o) => { await api.patch(`/leads/${id}`, { outcome: o === "none" ? null : o }); load(); };
  const del = async () => { if (!window.confirm("Delete this lead?")) return; try { await api.delete(`/leads/${id}`); toast.success("Lead deleted"); nav("/leads"); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div>
      <div className="px-6 lg:px-10 pt-8 pb-6 border-b bg-white">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="fade-up">
            <div className="label-xs mb-2">{[lead.business_categories?.join(" · ") || "Lead", lead.source].filter(Boolean).join(" · ")}</div>
            <h1 className="text-3xl lg:text-4xl font-extrabold tracking-tight" data-testid="lead-company-name">{lead.company_name}</h1>
            <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-600">
              <span className="font-medium text-slate-900">{fullName(lead)}</span>{lead.job_title && <span className="text-slate-500" data-testid="lead-job-title">· {lead.job_title}</span>}
              {lead.email && <a href={`mailto:${lead.email}`} className="flex items-center gap-1 hover:text-blue-600"><Mail size={14} />{lead.email}</a>}
              <FindEmailButton lead={lead} onSaved={load} />
              {lead.email_status === "bounced" && <span className="text-[10px] uppercase font-semibold bg-red-100 text-red-700 rounded px-1.5 py-0.5 self-center" data-testid="lead-bounced-badge">Bounced</span>}
              {lead.outcome === "unsubscribed" && <span className="text-[10px] uppercase font-semibold bg-slate-200 text-slate-700 rounded px-1.5 py-0.5 self-center" data-testid="lead-unsub-badge">Unsubscribed</span>}
              {lead.telephone && <span className="flex items-center gap-1"><Phone size={14} />{lead.telephone}</span>}
              {lead.website && <a href={lead.website.startsWith("http") ? lead.website : `https://${lead.website}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-blue-600"><Globe size={14} />{lead.website}</a>}
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setCompose(true)} data-testid="lead-compose-btn"><Send size={15} className="mr-1" />Email</Button>
            <Button variant="outline" onClick={() => setEdit(true)} data-testid="lead-edit-btn"><Pencil size={15} /></Button>
            {user?.role === "admin" && <Button variant="outline" onClick={del} data-testid="lead-delete-btn"><Trash2 size={15} /></Button>}
          </div>
        </div>
        <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="lead-stage-stepper">
          {STAGES.map((s) => (
            <button key={s.key} onClick={() => setStage(s.key)} data-testid={`stage-step-${s.key}`}
              className={`text-xs font-semibold px-3 py-2 rounded-lg border transition-colors ${lead.stage === s.key ? `stage-${s.key} ring-2 ring-offset-1 ring-blue-300` : "bg-white text-slate-500 hover:bg-slate-50"}`}>
              {s.n}. {s.label}</button>
          ))}
          <div className="ml-auto flex items-center gap-3">
            <Select value={lead.campaign_id || "none"} onValueChange={setCampaign}><SelectTrigger className="w-60 h-9" data-testid="lead-campaign-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">No campaign</SelectItem>{camps.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>
            <span className="text-sm text-slate-500">Score <ScoreChip score={lead.score} /></span>
            <Select value={lead.outcome || "none"} onValueChange={setOutcome}><SelectTrigger className="w-44 h-9" data-testid="lead-outcome-select"><SelectValue /></SelectTrigger>
              <SelectContent>{OUTCOMES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
          </div>
        </div>
      </div>

      <div className="p-6 lg:p-10 grid grid-cols-1 xl:grid-cols-12 gap-6">
        <div className="xl:col-span-7 space-y-6">
          <div className="bg-white border rounded-xl p-5" data-testid="ai-description-card">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2"><span className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-[11px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1"><Sparkles size={11} />AI</span><h3 className="font-bold">Company description</h3></div>
              <Button size="sm" variant="outline" onClick={describe} disabled={aiBusy} data-testid="ai-describe-btn" className="border-violet-300 text-violet-700">
                <Sparkles size={14} className="mr-1" />{aiBusy ? "Researching…" : lead.ai_description ? "Regenerate" : "Generate"}</Button>
            </div>
            {aiBusy && <div className="space-y-2 animate-pulse">{[1, 2, 3, 4].map((i) => <div key={i} className="h-3 bg-violet-100 rounded" />)}</div>}
            {!aiBusy && (lead.ai_description ? <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="ai-description-text">{lead.ai_description}</div>
              : <p className="text-sm text-slate-400">Generate a detailed description from the company name and website.</p>)}
          </div>
          <Tabs defaultValue="notes">
            <TabsList><TabsTrigger value="notes" data-testid="tab-notes">Sales notes</TabsTrigger><TabsTrigger value="emails" data-testid="tab-emails">Emails ({lead.sends.length})</TabsTrigger>
              <TabsTrigger value="replies" data-testid="tab-replies">Replies ({lead.replies.length})</TabsTrigger></TabsList>
            <TabsContent value="notes" className="pt-3"><Notes lead={lead} reload={load} /></TabsContent>
            <TabsContent value="emails" className="pt-3 space-y-2">
              {lead.sends.map((s) => (
                <div key={s.id} className="bg-white border rounded-lg p-3 text-sm flex flex-wrap items-center gap-3" data-testid={`send-${s.id}`}>
                  <div className="flex-1 min-w-0"><div className="font-medium truncate">{s.subject}</div><div className="text-xs text-slate-500">{fmtDate(s.sent_at)} · {s.sent_by_name}</div></div>
                  <span className={`text-[10px] uppercase font-semibold rounded px-1.5 py-0.5 ${s.status === "sent" ? "bg-emerald-100 text-emerald-700" : s.status === "simulated" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>{s.status}</span>
                  <span className="font-mono text-xs text-slate-500">{s.opens}o · {s.clicks}c · {s.visits}v</span>
                </div>))}
              {!lead.sends.length && <div className="text-sm text-slate-400">No emails yet.</div>}
            </TabsContent>
            <TabsContent value="replies" className="pt-3 space-y-3">
              <SimulateReply lead={lead} reload={load} />
              {lead.replies.map((r) => (
                <div key={r.id} className="bg-white border rounded-lg p-3 text-sm">
                  <div className="flex justify-between text-xs text-slate-500"><span className="uppercase font-semibold text-red-700">{r.classification?.category}</span><span>{fmtDate(r.received_at)}</span></div>
                  <div className="font-medium mt-1">{r.subject}</div><div className="text-slate-600 whitespace-pre-wrap mt-1">{r.body?.slice(0, 600)}</div>
                </div>))}
            </TabsContent>
          </Tabs>
        </div>
        <div className="xl:col-span-5 space-y-6">
          <div className="bg-white border rounded-xl p-5">
            <h3 className="font-bold mb-3">Engagement</h3>
            <div className="grid grid-cols-5 gap-2 text-center">
              {[["Sent", "emails_sent"], ["Opens", "opens"], ["Clicks", "clicks"], ["Visits", "visits"], ["Replies", "replys"]].map(([l, k]) => (
                <div key={k} className="bg-slate-50 rounded-lg p-2"><div className="text-xl font-extrabold">{lead.stats?.[k] || 0}</div><div className="label-xs !text-[9px]">{l}</div></div>))}
            </div>
            {lead.enrollments.length > 0 && <div className="mt-4 space-y-1">{lead.enrollments.map((e) => (
              <div key={e.id} className="text-xs text-slate-600 flex justify-between"><span>Campaign step {e.current_step + 1} · {e.status}</span><span>{e.status === "active" ? `next ${ago(e.next_run_at)}` : e.stopped_reason || ""}</span></div>))}</div>}
          </div>
          <div className="bg-white border rounded-xl p-5" data-testid="activity-timeline">
            <h3 className="font-bold mb-3">Activity timeline</h3>
            <div className="space-y-3 max-h-[560px] overflow-y-auto pr-1">
              {lead.activities.map((a) => (
                <div key={a.id} className="flex gap-3 text-sm"><div className="mt-1.5 h-2 w-2 rounded-full bg-blue-500 shrink-0" />
                  <div><div className="text-slate-700">{a.detail || a.type}</div><div className="text-xs text-slate-400">{a.type.replace("_", " ")} · {fmtDate(a.at)}</div></div></div>))}
              {!lead.activities.length && <div className="text-sm text-slate-400">No activity yet.</div>}
            </div>
          </div>
        </div>
      </div>
      <LeadFormDialog open={edit} onOpenChange={setEdit} lead={lead} onSaved={load} />
      <ComposeDialog lead={lead} open={compose} onOpenChange={setCompose} onSent={load} />
    </div>
  );
}
